/**
 * Календарный срок акции: пара дат `starts_at` / `ends_at` в формате Y-M-D.
 *
 * Обе даты необязательны и означают разное: пустое начало — «уже идёт»,
 * пустой конец — «бессрочно». `ends_at` включительно: «до 30 июня» значит,
 * что 30 июня скидка ещё работает.
 *
 * Сравнение — строками Y-M-D, а не через `Date`. Даты приходят с бэка без
 * времени и зоны; `new Date('2026-06-30')` разбирается как полночь UTC и в
 * часовом поясе Алматы (UTC+5) отъезжает на день назад. Лексикографический
 * порядок Y-M-D совпадает с хронологическим, так что строки честнее.
 */

export interface Period {
  starts_at?: string | null;
  ends_at?: string | null;
}

/** Состояние срока относительно сегодняшнего дня. */
export type PeriodState = 'running' | 'upcoming' | 'expired';

/** Сегодняшняя дата в формате Y-M-D по местному времени браузера. */
export function today(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/** Дата в виде «30 июня» либо «30 июня 2027», если год не текущий. */
export function formatDate(date: string): string {
  const [year, month, day] = date.split('-');
  const MONTHS = [
    'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
    'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
  ];
  const label = `${Number(day)} ${MONTHS[Number(month) - 1] ?? ''}`.trim();
  return year === today().slice(0, 4) ? label : `${label} ${year}`;
}

export function periodState(period: Period): PeriodState {
  const now = today();
  if (period.ends_at && period.ends_at < now) return 'expired';
  if (period.starts_at && period.starts_at > now) return 'upcoming';
  return 'running';
}

/** Срок одной строкой для таблицы: «Бессрочно», «до 30 июня», «с 1 июля». */
export function formatPeriod(period: Period): string {
  const { starts_at: from, ends_at: to } = period;
  if (!from && !to) return 'Бессрочно';
  if (from && to) return `${formatDate(from)} — ${formatDate(to)}`;
  if (to) return `до ${formatDate(to)}`;
  return `с ${formatDate(from!)}`;
}
